if (!customElements.get('toggle-products')) {
  customElements.define(
    'toggle-products',
    class extends HTMLElement {
      constructor() {
        super();
        this.boundHandleBtnClick = this.handleBtnClick.bind(this);
        this.boundHandlePopState = this.handlePopState.bind(this);
        this.isVariantRequestPending = false;
      }

      connectedCallback() {
        // Rendered `hidden` by default (see main-product.liquid) since Liquid can't see
        // the query string: a destination link (destination-product-handle.liquid) appends
        // ?data=fixed|unlimited when the destination is only actually covered by one plan
        // type — even if the landed-on product's own metafields link to both. When that
        // param is present we leave the toggle hidden instead of revealing then hiding it.
        const forcedType = new URLSearchParams(window.location.search).get('data');
        const productEl = document.querySelector('.product');

        if (forcedType === 'unlimited' || forcedType === 'fixed') {
          if (productEl) productEl.classList.add('product--single');
          // Nothing to reveal or fetch in the suppressed case — signal ready immediately
          // so main-product.liquid's skeleton-reveal script doesn't wait on it forever.
          this.dispatchEvent(new CustomEvent('toggle-products:ready'));
          return;
        }

        this.hidden = false;

        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.initialize(), { once: true });
          return;
        }
        this.initialize();
      }

      async initialize() {
        const handles = {
          unlimited: this.dataset.urlUnlimited,
          fixed: this.dataset.urlFixed,
        };

        if (!handles.unlimited || !handles.fixed) {
          this.dispatchEvent(new CustomEvent('toggle-products:ready'));
          return;
        }

        const sectionId = document
          .querySelector('product-info')
          ?.closest('section')
          ?.id.split('shopify-section-')[1];
        this.currentSectionElement = sectionId
          ? document.getElementById(`shopify-section-${sectionId}`)
          : null;

        if (!sectionId || !this.currentSectionElement) {
          this.dispatchEvent(new CustomEvent('toggle-products:ready'));
          return;
        }

        const entries = await Promise.all(
          Object.entries(handles).map(([type, handle]) =>
            this.productInfoRender(handle, type === 'unlimited', sectionId).then((content) => [
              type,
              content,
            ]),
          ),
        );

        this.sectionContents = Object.fromEntries(entries);
        if (Object.values(this.sectionContents).some((c) => !c)) {
          this.dispatchEvent(new CustomEvent('toggle-products:ready'));
          return;
        }

        const reviewsSectionEl = document
          .querySelector('[data-oke-widget]')
          ?.closest('.shopify-section');
        if (reviewsSectionEl) {
          const reviewsSectionId = reviewsSectionEl.id.split('shopify-section-')[1];
          const reviewsEntries = await Promise.all(
            Object.entries(handles).map(([type, handle]) =>
              this.reviewsSectionRender(handle, reviewsSectionId).then((content) => [
                type,
                content,
              ]),
            ),
          );
          this.reviewsSectionContents = Object.fromEntries(reviewsEntries);
        }

        const activeBtn = this.querySelector('button[data-type].active');
        this.currentType = activeBtn?.dataset.type ?? null;

        this.buttons = [...this.querySelectorAll('button.unlimited__btn, button.fixed__btn')];
        this.buttons.forEach((btn) => btn.addEventListener('click', this.boundHandleBtnClick));
        window.addEventListener('popstate', this.boundHandlePopState);

        const productInfo = document.querySelector('product-info');
        this.isVariantRequestPending = productInfo?.isRequestPending ?? false;
        // whenever product-info.js calls publish(PUB_SUB_EVENTS.productInfoRequestPending, { data: { pending } })
        // we update this.isVariantRequestPending with the same pending value
        this.unsubscribeRequestPending = subscribe(
          PUB_SUB_EVENTS.productInfoRequestPending,
          ({ data: { pending } }) => {
            // pending is a boolean indicating whether a variant request is currently pending
            // ensures that the buttons are disabled when a variant request is pending
            this.isVariantRequestPending = pending;
            this.updateButtonsDisabled();
          },
        );
        // Initially disable buttons if a variant request is already pending when toggle-products is initialized
        this.updateButtonsDisabled();

        // Toggle is now revealed, wired up, and its fetched variant content is cached —
        // safe for the PDP's loading skeleton to come off without a layout shift.
        this.dispatchEvent(new CustomEvent('toggle-products:ready'));
      }

      async productInfoRender(productHandle, unlimited, sectionId) {
        const [path, existingParams] = productHandle.split('?');
        const params = new URLSearchParams(existingParams);

        params.set('section_id', sectionId);
        if (unlimited) params.set('view', 'dynamic-esims');
        const fetchUrl = `/products/${path}?${params.toString()}`;

        try {
          const response = await fetch(fetchUrl);

          if (!response.ok) throw new Error('Failed to fetch section');

          const htmlText = await response.text();
          const parsedHtml = new DOMParser().parseFromString(htmlText, 'text/html');
          return parsedHtml.getElementById(`shopify-section-${sectionId}`);
        } catch (error) {
          console.error('Error rendering product section:', error);
        }
      }

      async reviewsSectionRender(productHandle, reviewsSectionId) {
        const [path, existingParams] = productHandle.split('?');
        const params = new URLSearchParams(existingParams);
        params.set('section_id', reviewsSectionId);
        const fetchUrl = `/products/${path}?${params.toString()}`;

        try {
          const response = await fetch(fetchUrl);
          if (!response.ok) throw new Error('Failed to fetch reviews section');
          const htmlText = await response.text();
          const parsedHtml = new DOMParser().parseFromString(htmlText, 'text/html');
          return parsedHtml.getElementById(`shopify-section-${reviewsSectionId}`);
        } catch (error) {
          console.error('Error rendering reviews section:', error);
        }
      }

      handlePopState() {
        if (this.classList.contains('loading')) return;

        const currentPath = window.location.pathname;
        const unlimitedPath = `/products/${this.dataset.urlUnlimited.split('?')[0]}`;
        const fixedPath = `/products/${this.dataset.urlFixed.split('?')[0]}`;

        let type;
        if (currentPath === unlimitedPath) {
          type = 'unlimited';
        } else if (currentPath === fixedPath) {
          type = 'fixed';
        } else {
          return;
        }

        if (type === this.currentType) return;

        this.classList.add('loading');
        this.buttons.forEach((btn) => {
          btn.classList.remove('active');
          if (btn.classList.contains(`${type}__btn`)) btn.classList.add('active');
        });
        this.updateButtonsDisabled();

        this.applyTypeSwitch(type);
      }

      async handleBtnClick(event) {
        event.preventDefault();

        const typeFromClass = [...event.target.classList]
          .find((item) => item.includes('__btn'))
          ?.replace('__btn', '');
        const type = event.target.dataset.type || typeFromClass;

        if (!type || type === this.currentType) return;

        this.classList.add('loading');
        this.buttons.forEach((btn) => {
          btn.classList.remove('active');
          if (btn === event.target) btn.classList.add('active');
        });
        this.updateButtonsDisabled();

        let url;
        if (type === 'unlimited') {
          url = `/products/${this.dataset.urlUnlimited}?days=${this.days ? this.days : 1}`;
        } else {
          let newUrl = new URL(window.location.href);
          let searchParams = new URLSearchParams(newUrl.search);

          searchParams.get('days') && (this.days = searchParams.get('days'));

          const fixedUrl = this.dataset.urlFixed;
          if (fixedUrl.includes('variant=')) {
            url = `/products/${fixedUrl}`;
          } else {
            const variantScript =
              this.sectionContents.fixed?.querySelector('[data-selected-variant]');
            const variantId = variantScript ? JSON.parse(variantScript.textContent)?.id : null;
            url = variantId
              ? `/products/${fixedUrl}?variant=${variantId}`
              : `/products/${fixedUrl}`;
          }
        }

        history.pushState(null, null, url);

        this.applyTypeSwitch(type);
      }

      applyTypeSwitch(type) {
        const productInfo = document.querySelector('product-info');
        productInfo?.cancelPendingRequest();
        if (productInfo) productInfo.dataset.url = window.location.pathname;

        const currentVariantPicker = this.currentSectionElement.querySelector('.variant__picker');
        const currentKeyInfo = this.currentSectionElement.querySelector('key-info-popup');
        const currentReviewInfo = this.currentSectionElement.querySelector(
          'div[data-oke-star-rating]',
        );
        const currentCountries =
          this.currentSectionElement.querySelector('countries-covered-popup');
        const reviewsSectionEl = document
          .querySelector('[data-oke-widget]')
          ?.closest('.shopify-section');
        const currentWidget = reviewsSectionEl?.querySelector('[data-oke-widget]');

        // product-info.js hides the old <variant-selects> for 500ms before removing it
        // (see HTMLUpdateUtility.viewTransition). If we snapshot mid-switch we'd clone that
        // leftover copy right along with the live one and it'd stick around in the cache
        // forever, causing duplicate radios with the same name/form further down the line.
        currentVariantPicker
          ?.querySelectorAll('variant-selects[style*="display: none"]')
          .forEach((stale) => stale.remove());

        const outgoingCachedPicker =
          this.sectionContents[this.currentType]?.querySelector('.variant__picker');
        if (outgoingCachedPicker && currentVariantPicker) {
          outgoingCachedPicker.replaceWith(currentVariantPicker.cloneNode(true));
        }

        const currentTitle = document.querySelector('.product__title');
        const outgoingCachedTitle =
          this.sectionContents[this.currentType]?.querySelector('.product__title');
        if (outgoingCachedTitle && currentTitle) {
          outgoingCachedTitle.replaceWith(currentTitle.cloneNode(true));
        }

        const cloneNode = (className) =>
          this.sectionContents[type]?.querySelector(className)?.cloneNode(true);

        let fetchedVariantPicker = cloneNode('.variant__picker');
        // Same cleanup as above, in case an older cache entry already picked up a stale copy.
        fetchedVariantPicker
          ?.querySelectorAll('variant-selects[style*="display: none"]')
          .forEach((stale) => stale.remove());
        let fetchedTitle = cloneNode('.product__title');
        let fetchedKeyInfo = cloneNode('key-info-popup');
        let fetchedCountriesCovered = cloneNode('countries-covered-popup');
        let fetchedReviewInfo = cloneNode('[data-oke-star-rating]');
        let fetchedWidget = this.reviewsSectionContents?.[type]
          ?.querySelector('[data-oke-widget]')
          ?.cloneNode(true);
        if (fetchedWidget) fetchedWidget.removeAttribute('data-oke-rendered');

        const pickerFirstDiv = currentVariantPicker?.querySelector(':scope > div');
        if (pickerFirstDiv) {
          pickerFirstDiv.classList.add('variant__picker--skeleton');
        }

        currentReviewInfo?.classList.add('review-summary--skeleton');

        setTimeout(() => {
          if (fetchedTitle && currentTitle) {
            currentTitle.replaceWith(fetchedTitle);
          }
          if (fetchedVariantPicker && currentVariantPicker) {
            currentVariantPicker.replaceWith(fetchedVariantPicker);
          } else if (pickerFirstDiv) {
            pickerFirstDiv.classList.remove('variant__picker--skeleton');
          }
          if (fetchedKeyInfo && currentKeyInfo) {
            currentKeyInfo.replaceWith(fetchedKeyInfo);
          }
          if (fetchedCountriesCovered && currentCountries) {
            currentCountries.replaceWith(fetchedCountriesCovered);
          }
          if (fetchedReviewInfo && currentReviewInfo) {
            const reviewIsElement = fetchedReviewInfo.nodeType === 1;
            if (reviewIsElement) {
              fetchedReviewInfo.querySelector('[data-oke-container]')?.remove();
              fetchedReviewInfo.removeAttribute('data-oke-rendered');
              fetchedReviewInfo.classList.add('review-summary--skeleton');
            }
            currentReviewInfo.replaceWith(fetchedReviewInfo);
            if (reviewIsElement) {
              const isReady = () =>
                !!fetchedReviewInfo.querySelector('.oke-stars-foreground[style]');
              const revealReview = () =>
                fetchedReviewInfo.classList.remove('review-summary--skeleton');
              const obs = new MutationObserver((_, o) => {
                if (!isReady()) return;
                o.disconnect();
                clearTimeout(reviewFallback);
                revealReview();
              });
              obs.observe(fetchedReviewInfo, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['style'],
              });
              const reviewFallback = setTimeout(() => {
                obs.disconnect();
                revealReview();
              }, 1000);
            }
          }
          if (fetchedWidget && currentWidget) {
            currentWidget.replaceWith(fetchedWidget);
          }
          if (fetchedReviewInfo || fetchedWidget) {
            window.okeWidgetApi?.initAllWidgets?.();
          }
          const faqSection = document.getElementById('faq-section');
          const faqTemplate = document.getElementById(`toggle-faq-${type}`);
          if (faqSection && faqTemplate) {
            const currentContainers = [...faqSection.querySelectorAll('.question-container')];
            const activeIndex = currentContainers.findIndex((c) =>
              c.classList.contains('container-active'),
            );
            const newContainers = [
              ...faqTemplate.content.cloneNode(true).querySelectorAll('.question-container'),
            ];
            if (activeIndex >= 0) {
              newContainers.forEach((c, i) => {
                c.classList.toggle('container-active', i === activeIndex);
                c.setAttribute('aria-selected', i === activeIndex ? 'true' : 'false');
              });
            }
            currentContainers.forEach((container, i) => {
              if (newContainers[i]) container.replaceWith(newContainers[i]);
            });
            faqSection.dispatchEvent(new CustomEvent('faq:swap'));
          }
          this.currentType = type;
          this.classList.remove('loading');
          this.updateButtonsDisabled();

          // product-info.js may re-render the section when dataset.url changes,
          // which re-injects liquid-rendered flag icons. Remove any beyond the first pair.
          const infoContainer = document.querySelector('.product__info-container');
          if (infoContainer) {
            const flagWrappers = [...infoContainer.children].filter((el) =>
              el.classList.contains('cv__flag-icon-wrapper'),
            );
            flagWrappers.slice(2).forEach((el) => el.remove());
          }

          // product-info.js re-render also re-injects section script tags — de-duplicate by src.
          if (this.currentSectionElement) {
            const seen = new Set();
            this.currentSectionElement.querySelectorAll('script[src]').forEach((s) => {
              const src = s.getAttribute('src');
              if (seen.has(src)) s.remove();
              else seen.add(src);
            });
          }
        }, 300);
      }

      updateButtonsDisabled() {
        if (!this.buttons) return;
        const disabled = this.classList.contains('loading') || this.isVariantRequestPending;
        this.buttons.forEach((btn) => (btn.disabled = disabled));
      }

      disconnectedCallback() {
        this.buttons?.forEach((btn) => btn.removeEventListener('click', this.boundHandleBtnClick));
        window.removeEventListener('popstate', this.boundHandlePopState);
        this.unsubscribeRequestPending?.();
      }
    },
  );
}
